// components/views/PicPaymentView.tsx
import { useEffect, useState, useRef } from "react";
import { PaymentMethodCard } from "@/components/private/PaymentMethodCard";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
    Users2,
    PackageCheck,
    Wallet2,
    Info,
    CreditCard,
    CheckCircle2,
    Tags,
    Loader2,
    Zap,
    ArrowRight,
    AlertTriangle // Icon baru ditambahkan untuk alert
} from "lucide-react";
import type { UserData, SummaryData, PicApiResponse } from "@/types/pic";
import type { PaymentData } from "@/types/payment";
import StepNavigation from "@/components/private/StepNavigation";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AxiosError } from "axios";

interface PicPaymentViewProps {
    user: UserData;
    activePeriodId: number;
    onNext: () => void;
    onBack: () => void;
    backDisabled?: boolean;
}

interface LaravelErrorResponse {
    message?: string;
}

export const PicPaymentView = ({ user, activePeriodId, onNext, onBack, backDisabled = false }: PicPaymentViewProps) => {
    const queryClient = useQueryClient();
    const [selectedMethod, setSelectedMethod] = useState<string>("8");
    const [payment, setPayment] = useState<PaymentData | null>(null);
    const [summary, setSummary] = useState<SummaryData | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // STATE BARU: Untuk kontrol modal dan menahan Promise
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const promiseRef = useRef<{ resolve: () => void; reject: (reason?: unknown) => void } | null>(null);

    const formatCurrency = (amount: number | string) => {
        const value = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat("id-ID", {
            style: "currency", currency: "IDR", minimumFractionDigits: 0,
        }).format(value);
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const [payRes, sumRes] = await Promise.all([
                    axiosClient.get<{ data: PaymentData }>(`/participants/${user.participant_id}/registrations/period/${activePeriodId}/payment`),
                    axiosClient.get<PicApiResponse>(`/pic/participants`, { params: { period_id: activePeriodId, status: "unpaid" } })
                ]);

                if (payRes.data.data) {
                    setPayment(payRes.data.data);
                    setSelectedMethod(payRes.data.data.gateway_transaction_id);
                }
                if (sumRes.data.summary) setSummary(sumRes.data.summary);
            } catch (error: unknown) {
                console.error("Sync Error:", error);
            } finally { setLoading(false); }
        };
        fetchAllData();
    }, [activePeriodId, user.participant_id]);


    // 1. Fungsi Asli Eksekusi API (dipanggil jika user setuju di popup)
    const executePayment = async (): Promise<void> => {
        setIsSubmitting(true);
        try {
            const { data: regRes } = await axiosClient.get(`/participants/${user.participant_id}/registrations/period/${activePeriodId}`);
            if (!regRes.data?.registration_id) throw new Error("Registration ID not found");

            await toast.promise(
                axiosClient.post(`/payments/registrations/${regRes.data.registration_id}`, {
                    gateway_transaction_id: selectedMethod
                }),
                {
                    loading: "Memproses tagihan kolektif...",
                    success: "Tagihan VA berhasil diterbitkan!",
                    error: (err: AxiosError<LaravelErrorResponse>) =>
                        err.response?.data?.message || "Gagal memproses pendaftaran tim."
                }
            );

            await queryClient.invalidateQueries({ queryKey: ["registration-step"] });
            await queryClient.invalidateQueries({ queryKey: ["registration-status"] });

            onNext();
        } catch (err: unknown) {
            console.error(err);
            throw err; // Lempar error agar promise dari StepNavigation ikut di-catch
        } finally {
            setIsSubmitting(false);
        }
    };


    // 2. Fungsi Interceptor (Menggantikan handleSubmit lama)
    const handlePromptConfirm = (): Promise<void> => {
        // Jika tagihan sudah aktif, abaikan popup dan langsung next
        if (backDisabled) {
            onNext();
            return Promise.resolve();
        }

        if (!selectedMethod) {
            toast.error("Pilih metode pembayaran.");
            return Promise.reject(new Error("No method selected"));
        }

        // Tampilkan popup konfirmasi
        setShowConfirmModal(true);

        // Kembalikan Promise yang akan ditahan (pending) 
        // sampai user mengklik tombol "Ya" atau "Batal" di Modal
        return new Promise((resolve, reject) => {
            promiseRef.current = { resolve, reject };
        });
    };

    // 3. Aksi saat User Setuju di Modal
    const onConfirmModal = async () => {
        setShowConfirmModal(false);
        try {
            await executePayment();
            // Jika berhasil, resolve promise yang ditahan StepNavigation
            if (promiseRef.current) promiseRef.current.resolve();
        } catch (error: unknown) {
            if (promiseRef.current) promiseRef.current.reject(error);
        }
    };

    // 4. Aksi saat User Batal di Modal
    const onCancelModal = () => {
        setShowConfirmModal(false);
        // Reject promise agar loading state di StepNavigation berhenti
        if (promiseRef.current) promiseRef.current.reject(new Error("User cancelled"));
    };

    if (loading) return <PicPaymentSkeleton />;

    return (
        <div className="space-y-10 pb-32">

            {/* (Bagian UI Atas, Metode Pembayaran, dll tetap sama dengan kode asli Anda) */}

            <section className="space-y-6">
                <div className="flex items-center gap-4">
                    <div className="bg-blue-600 p-2 rounded-lg shadow-lg shadow-blue-200 dark:shadow-none">
                        <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white uppercase">
                        Pilih Metode Pembayaran <span className="text-blue-600">Bank</span>
                    </h3>
                    <div className="h-[1px] flex-1 bg-gradient-to-r from-slate-200 to-transparent dark:from-slate-800" />
                </div>

                <div className="grid gap-5 md:grid-cols-0">
                    <PaymentMethodCard
                        id="8"
                        name="VA BNI (Rupiah)"
                        description="Transfer bank lokal atau e-wallet Indonesia."
                        logoUrl="/payment/bni.png"
                        badgeText="IDR"
                        badgeColor="orange"
                        selected={selectedMethod === "8"}
                        onSelect={setSelectedMethod}
                        disabled={backDisabled}
                    />
                    {/* <PaymentMethodCard
                        id="9"
                        name="VA BNI (Dollar)"
                        description="Khusus peserta internasional (Wire Transfer)."
                        logoUrl="/payment/bni.png"
                        badgeText="USD"
                        badgeColor="green"
                        selected={selectedMethod === "9"}
                        onSelect={setSelectedMethod}
                        disabled={backDisabled}
                    /> */}
                </div>
            </section>

            {/* 2. RINGKASAN DATA & TOTAL */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-1 border-0 bg-white dark:bg-slate-900 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform duration-500">
                        <Users2 className="w-24 h-24" />
                    </div>
                    <CardContent className="p-8 space-y-6 relative z-10">
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full">
                                Anggota Grup
                            </span>
                            <Info className="w-4 h-4 text-slate-300" />
                        </div>
                        <div className="flex items-baseline gap-2">
                            <h2 className="text-5xl font-black tracking-tighter text-slate-900 dark:text-white">
                                {summary?.total_unpaid ?? 0}
                            </h2>
                            <span className="text-slate-400 font-bold text-sm">/ {summary?.total_participants ?? 0}</span>
                        </div>
                        <p className="text-xs font-bold text-slate-500 leading-relaxed uppercase tracking-wider">
                            Peserta belum lunas.
                        </p>
                    </CardContent>
                </Card>

                <Card className="lg:col-span-2 border-0 bg-slate-900 text-white shadow-2xl relative overflow-hidden flex flex-col justify-center min-h-[220px]">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent opacity-50" />
                    <CardContent className="p-8 space-y-6 relative z-10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Total Nominal Tagihan</span>
                            </div>
                            <Wallet2 className="w-5 h-5 text-blue-500" />
                        </div>
                        <div className="space-y-1">
                            <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
                                {formatCurrency(payment?.amount || summary?.total_biaya || 0)}
                            </h2>
                            <p className="text-[10px] text-slate-400 font-bold italic opacity-60">*Harga paket akumulasi seluruh peserta pendaftaran</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Badge className="bg-blue-600 text-white border-0 px-4 py-1 text-[10px] font-black tracking-widest uppercase">
                                {payment?.status || "Ready to Pay"}
                            </Badge>
                            {payment?.status === 'unpaid' && (
                                <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3 text-green-500" /> VA Aktif
                                </span>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* 3. RINCIAN PER PAKET */}
            <Card className="border-0 bg-white dark:bg-slate-900 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl overflow-hidden">
                <CardHeader className="p-8 pb-4 flex flex-row items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded-lg">
                            <PackageCheck className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                            <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Rincian Paket Tim</h4>
                            <p className="text-[10px] text-slate-400 font-medium italic">Daftar akumulasi paket yang akan dibayarkan</p>
                        </div>
                    </div>
                    <Tags className="w-5 h-5 text-slate-200" />
                </CardHeader>

                <CardContent className="p-8 pt-0">
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {summary?.participants_per_package?.map((pkg) => (
                            <div key={pkg.package_id} className="group flex items-center justify-between py-5 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 px-2 -mx-2 rounded-xl transition-colors">
                                <div className="flex items-center gap-4">
                                    <div className="h-10 w-10 shrink-0 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center font-black text-blue-600 border border-blue-100">
                                        {pkg.total}
                                    </div>
                                    <div className="space-y-0.5">
                                        <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{pkg.package_name}</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                            {pkg.total} Peserta × {formatCurrency(pkg.total_price / pkg.total)}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-black text-slate-900 dark:text-white font-mono tracking-tighter">
                                        {formatCurrency(pkg.total_price)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 pt-6 border-t-2 border-dashed border-slate-100 flex justify-between items-center">
                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Estimasi Total</span>
                        <span className="text-lg font-black text-blue-600 font-mono">{formatCurrency(summary?.total_biaya ?? 0)}</span>
                    </div>
                </CardContent>
            </Card>

            {/* 🔹 TOMBOL AKSI UTAMA DI KARTU */}
            <div className="pt-4 space-y-3">
                {backDisabled ? (
                    <Button
                        onClick={() => onNext()}
                        className="w-full h-14 text-sm font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-200 dark:shadow-none transition-all active:scale-[0.98]"
                    >
                        Lihat Instruksi Pembayaran
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                ) : (
                    <Button
                        onClick={handlePromptConfirm} // DIUBAH KE handlePromptConfirm
                        disabled={isSubmitting || !selectedMethod}
                        className="w-full h-14 text-sm font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-200 dark:shadow-none transition-all active:scale-[0.98] group"
                    >
                        {isSubmitting ? (
                            <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                        ) : (
                            <Zap className="mr-2 h-6 w-6 fill-current group-hover:text-yellow-300 transition-colors" />
                        )}
                        Buat Tagihan Sekarang
                    </Button>
                )}
            </div>

            {/* 🔹 STEP NAVIGATION DI BAWAH (Integrasi Otomatis) */}
            <StepNavigation
                onBack={onBack}
                onNext={handlePromptConfirm} // DIUBAH KE handlePromptConfirm
                nextDisabled={!selectedMethod}
                backDisabled={backDisabled}
            />

            {/* MODAL KONFIRMASI (Lebih Cantik & Modern) */}
            {showConfirmModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
                    <Card className="w-full max-w-[800px] border-0 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 bg-white dark:bg-slate-900 relative">

                        {/* Dekorasi Glow di Background Modal */}
                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

                        <div className="relative p-6 sm:p-8 space-y-6">

                            {/* Ikon Tengah (Lebih Menonjol) */}
                            <div className="flex justify-center">
                                <div className="bg-amber-100 dark:bg-amber-900/30 p-4 rounded-full ring-8 ring-amber-50 dark:ring-amber-900/10">
                                    <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-500" />
                                </div>
                            </div>

                            {/* Teks Judul & Deskripsi */}
                            <div className="text-center space-y-2">
                                <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">
                                    Konfirmasi Tagihan Kolektif
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed px-2">
                                    Pastikan data berikut benar sebelum nomor Virtual Account diterbitkan.
                                </p>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-3">
                                <div className="rounded-3xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-950">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Total Nominal Tagihan</p>
                                    <p className="mt-3 text-lg font-black text-slate-900 dark:text-white">
                                        {formatCurrency(payment?.amount || summary?.total_biaya || 0)}
                                    </p>
                                </div>
                                <div className="rounded-3xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-950">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Anggota Grup</p>
                                    <p className="mt-3 text-lg font-black text-slate-900 dark:text-white">
                                        {summary?.total_unpaid ?? 0} / {summary?.total_participants ?? 0}
                                    </p>
                                </div>
                                <div className="rounded-3xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-950">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Metode Pembayaran</p>
                                    <p className="mt-3 text-lg font-black text-slate-900 dark:text-white">
                                        {selectedMethod === "8"
                                            ? "VA BNI (Rupiah)"
                                            : selectedMethod === "9"
                                            ? "VA BNI (Dollar)"
                                            : selectedMethod}
                                    </p>
                                </div>
                            </div>

                            {/* Kotak Info Warning (Desain Baru) */}
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200/70 dark:border-amber-800/50 rounded-3xl p-4 flex items-start gap-3 shadow-sm">
                                <Info className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                                <div className="text-sm font-semibold text-amber-800 dark:text-amber-200 leading-relaxed">
                                    Setelah VA diterbitkan, perubahan paket, jumlah peserta, dan metode pembayaran tidak dapat dilakukan.
                                </div>
                            </div>

                            {/* Tombol Aksi (Grid 50:50 dengan Tinggi Ekstra) */}
                            <div className="grid grid-cols-2 gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    className="w-full h-12 font-bold border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-all"
                                    onClick={onCancelModal}
                                    disabled={isSubmitting}
                                >
                                    Batal
                                </Button>
                                <Button
                                    className="w-full h-12 font-bold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98]"
                                    onClick={onConfirmModal}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                                    {isSubmitting ? "Memproses..." : "Ya, Buat Tagihan"}
                                </Button>
                            </div>

                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

// ... (PicPaymentSkeleton tetap sama)
const PicPaymentSkeleton = () => (
    <div className="space-y-10 pb-32">
        <div className="space-y-4">
            <Skeleton className="h-4 w-40" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Skeleton className="h-28 rounded-2xl" />
                <Skeleton className="h-28 rounded-2xl" />
            </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-56 rounded-3xl" />
            <Skeleton className="h-56 lg:col-span-2 rounded-3xl" />
        </div>
        <Skeleton className="h-40 rounded-3xl" />
    </div>
);